import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'

import ErrorMessage from 'components/ui/ErrorMessage'
import SearchButton from 'components/form/SearchButton'

import { selectClub } from 'store/club/slice'
import { selectCourse } from 'store/course/slice'
import { transferCourse } from 'store/course/thunks'
import { clearCourses, selectCourses } from 'store/courses/slice'
import { searchCourses } from 'store/courses/thunks'

export default function TransferCourseToClub(): JSX.Element {
  const [transferId, setTransferId] = useState<number | null>(null)
  const [transferInitiated, setTransferInitiated] = useState<boolean>(false)
  const inputRef = useRef<any>()
  const dispatch = useDispatch()
  const intl = useIntl()

  const { saveStatus, saveError, course } = useSelector(selectCourse)
  const { status, courses } = useSelector(selectCourses)
  const { club } = useSelector(selectClub)

  useEffect(() => {
    if (saveStatus === 'succeeded' && transferInitiated) {
      setTransferInitiated(false)
      setTransferId(null)
      if (inputRef?.current) {
        inputRef.current.value = ''
      }
    }
  }, [course, transferInitiated, transferId, saveStatus])

  const submitSearchCourses = () => {
    if (transferId) {
      setTransferInitiated(true)
      dispatch(clearCourses())
      dispatch(
        searchCourses({
          data: { courseId: transferId },
        }),
      )
    }
  }

  const isNewCourseInvalid = (c: Course) => {
    const clubProviderAbbr = club.provider?.abbreviation || ''
    return (
      c.provider?.id !== club.provider?.id && c.provider?.options?.allowedParents.includes(clubProviderAbbr) === false
    )
  }

  const getTransferCourseInfo = (c: Course) => {
    const { name, club, provider } = c
    const clubName = club ? ` (${club.name})` : ''
    const courseProvider = provider ? `, ${provider.abbreviation}` : ''
    return `${name}${courseProvider}${clubName}`
  }

  const submitTransferCourse = () => {
    const confirmText = intl.formatMessage({ id: 'strings.confirmTransferCourseToNewClub' })

    if (!window.confirm(confirmText)) {
      return
    }

    if (club.id && transferId) {
      dispatch(transferCourse({ courseId: transferId, data: { clubId: club.id } }))
    }
  }

  return (
    <TransferForm>
      <span>{intl.formatMessage({ id: 'strings.transferExistingCourseToClub' })}</span>
      <input
        ref={inputRef}
        type="text"
        placeholder="Course ID"
        onChange={(e) => setTransferId(parseInt(e.target.value))}
      />
      <SearchButton status={status} text={intl.formatMessage({ id: 'strings.search' })} onClick={submitSearchCourses} />

      {transferInitiated && (
        <Results>
          {courses.map((c: Course) => (
            <li>
              {getTransferCourseInfo(c)}
              <TransferButton
                status={saveStatus}
                text={intl.formatMessage({ id: 'strings.transfer' })}
                onClick={submitTransferCourse}
                disabled={isNewCourseInvalid(c)}
              />
              {isNewCourseInvalid(c) && (
                <TransferError>{intl.formatMessage({ id: 'strings.providerMappingMismatch' })}</TransferError>
              )}
              {saveError && saveStatus === 'failed' && <TransferError>{saveError}</TransferError>}
            </li>
          ))}
        </Results>
      )}
    </TransferForm>
  )
}

const TransferForm = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  & > span {
    color: ${(props) => props.theme.palette.primary.main};
  }
  & > input {
    height: 30px;
    margin-top: 10px;
    padding: 0 10px;

    &::placeholder {
      color: ${(props) => props.theme.customPalette.mediumGray};
    }
  }
  & > button {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.customPalette.light};
    height: 35px;
    border: 1px solid;
    border-color: ${(props) => props.theme.palette.primary.main};
    border-radius: 5px;
    margin-top: 10px;
    cursor: pointer;
    margin-left: 5px;

    &:not(.disabled):hover {
      opacity: 0.85;
    }

    &.disabled {
      background-color: ${(props) => props.theme.customPalette.mediumGray};
      border-color: ${(props) => props.theme.customPalette.mediumGray};
      cursor: unset;
    }
  }
`

const TransferButton = styled(SearchButton)`
  height: 20px;
  font-size: 12px;
  padding: 0 10px;
  min-width: 70px;
  margin-left: 10px;
  background-color: transparent;
  color: ${(props) => props.theme.palette.primary.main};
  border: 1px solid;
  border-color: ${(props) => props.theme.palette.primary.main};

  &:hover {
    background-color: transparent;
    opacity: 0.85;
  }
`

const Results = styled.ul`
  width: 100%;
  margin-top: 10px;
  font-size: 14px;
`

const TransferError = styled(ErrorMessage)`
  margin-top: 10px;
`
